import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithoutRef, ElementType } from "react";
import { cn } from "@/shared/lib/cn";

/**
 * Typography primitive. Every text role in DESIGN.md §3 is a variant here, so a
 * spec change updates one line and every usage follows. Fraunces (serif) carries
 * voice; Inter (sans) carries chrome — the variant picks the right family.
 *
 * Polymorphic via `as`; sensible semantic defaults per variant (override for
 * correct heading levels).
 */
export const textVariants = cva("", {
  variants: {
    variant: {
      display:
        "font-serif font-normal text-[clamp(38px,6.2vw,64px)] leading-[1.02] tracking-[-0.025em] text-ink",
      subhead: "font-serif font-normal text-lg leading-[1.5] text-ink-muted",
      wordmark: "font-serif font-bold text-[22px] tracking-[-0.015em] text-ink",
      h4: "font-serif font-medium text-xl tracking-[-0.015em] text-ink",
      lede: "font-serif font-normal text-[18.5px] leading-[1.5] text-ink",
      userBody: "font-serif font-normal text-[17px] leading-[1.5] text-ink",
      body: "font-sans text-[15px] leading-[1.55] text-ink-soft",
      label:
        "font-sans font-semibold text-[10.5px] uppercase tracking-[0.14em] text-ink-faint",
      ui: "font-sans font-medium text-sm text-ink",
    },
  },
  defaultVariants: { variant: "body" },
});

const defaultTag: Record<NonNullable<VariantProps<typeof textVariants>["variant"]>, ElementType> = {
  display: "h1",
  subhead: "p",
  wordmark: "span",
  h4: "h4",
  lede: "p",
  userBody: "p",
  body: "p",
  label: "span",
  ui: "span",
};

type TextProps<E extends ElementType> = {
  as?: E;
} & VariantProps<typeof textVariants> &
  Omit<ComponentPropsWithoutRef<E>, "as" | keyof VariantProps<typeof textVariants>>;

export function Text<E extends ElementType = "p">({
  as,
  className,
  variant,
  ...props
}: TextProps<E>) {
  const Comp = (as ?? defaultTag[variant ?? "body"]) as ElementType;
  return <Comp className={cn(textVariants({ variant }), className)} {...props} />;
}
